import { useCallback, useEffect, useState } from 'react';
import { api, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import AlisDetay from './AlisDetay';
import YeniAlis from './YeniAlis';
import ParasutEsleme from './ParasutEsleme';

type Fatura = {
  id: number; no: string; date: string; supplier: string;
  totalKurus: number; status: string; satirAdedi: number; bekleyenSatir: number;
  itirazKalanGun: number; artisVar: boolean;
};
type Veri = { faturalar: Fatura[]; ozet: { bekleyenFatura: number; bekleyenSatir: number }; itirazGun: number };

type Artis = {
  id: number; code: string; name: string; artisYuzde: number;
  unitPriceKurus: number; faturaNo: string; tarih: string; tedarikci: string;
  itirazKalanGun: number; penceseKapandi: boolean;
};

/**
 * ALIŞ FATURALARI — "Faturaya Bağla" akışı (şartname 4.2).
 *
 * Fiyat artışları ayrı sekmede: itiraz penceresi 7 gün ve kaçırılan her gün
 * para kaybı. Bu yüzden kalan gün sayacı kartın üstünde duruyor.
 */
export default function Alis() {
  const { can } = useAuth();
  const [sekme, setSekme] = useState<'bekleyen' | 'hepsi' | 'artis' | 'esleme'>('bekleyen');
  const [veri, setVeri] = useState<Veri | null>(null);
  const [artislar, setArtislar] = useState<{ esik: number; itirazGun: number; satirlar: Artis[] } | null>(null);
  const [secili, setSecili] = useState<number | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const yukle = useCallback(() => {
    if (sekme === 'esleme') return;
    if (sekme === 'artis') {
      api<typeof artislar>('/api/alis/fiyat-artislari')
        .then(setArtislar).catch((e) => setHata((e as Error).message));
    } else {
      api<Veri>(`/api/alis?durum=${sekme}`).then(setVeri).catch((e) => setHata((e as Error).message));
    }
  }, [sekme]);
  useEffect(yukle, [yukle]);

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="ccd-title">Alış Faturaları</h1>
          <div className="ccd-meta">
            Satırı ürüne bağla → tedarikçi kodu takma ad olur, stok girer, fiyat geçmişi yazılır.
            İkinci faturada bir daha sormaz.
          </div>
        </div>
        {can('purchase.match') && (
          <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setYeniAcik(true)} data-tur="elle-alis">
            + Elle Alış Faturası
          </button>
        )}
      </div>

      {hata && (
        <div className="ccd-banner ccd-banner-danger">
          <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
        </div>
      )}

      {veri && veri.ozet.bekleyenSatir === 0 && sekme === 'bekleyen' && (
        <div className="ccd-banner">
          <span className="ccd-banner-icon" aria-hidden="true">✓</span>
          <div className="ccd-meta">
            Bağlanmayı bekleyen satır yok. Paraşüt hattı açıldığında gelen e-faturalar
            buraya düşecek; o zamana kadar elle giriş yapılabilir.
          </div>
        </div>
      )}

      <div className="ccd-tabs" role="tablist">
        <button role="tab" aria-selected={sekme === 'bekleyen'} className="ccd-tab"
                onClick={() => setSekme('bekleyen')} data-tur="sekme-bekleyen">
          Bağlanmayı bekleyen{veri ? ` (${veri.ozet.bekleyenFatura})` : ''}
        </button>
        <button role="tab" aria-selected={sekme === 'hepsi'} className="ccd-tab"
                onClick={() => setSekme('hepsi')}>Tüm faturalar</button>
        <button role="tab" aria-selected={sekme === 'artis'} className="ccd-tab"
                onClick={() => setSekme('artis')} data-tur="sekme-artis">Fiyat artışları</button>
        <button role="tab" aria-selected={sekme === 'esleme'} className="ccd-tab"
                onClick={() => setSekme('esleme')} data-tur="sekme-esleme">Paraşüt ürün eşleme</button>
      </div>

      {sekme === 'esleme' ? (
        <ParasutEsleme />
      ) : sekme === 'artis' ? (
        !artislar ? <div className="ccd-inline-note">Yükleniyor…</div>
        : artislar.satirlar.length === 0 ? (
          <div className="ccd-empty">
            %{artislar.esik} üzerinde fiyat artışı yok.
          </div>
        ) : (
          <div className="ccd-table-wrap">
            <table className="ccd-table">
              <thead>
                <tr>
                  <th className="ccd-th">Ürün</th>
                  <th className="ccd-th ccd-td-num">Artış <Ipucu id="fiyat-artisi" /></th>
                  <th className="ccd-th ccd-td-num">Yeni birim</th>
                  <th className="ccd-th">Fatura</th>
                  <th className="ccd-th">İtiraz penceresi <Ipucu id="fiyat-artisi" /></th>
                </tr>
              </thead>
              <tbody>
                {artislar.satirlar.map((a) => (
                  <tr key={a.id} className="ccd-tr">
                    <td className="ccd-td">
                      <b className="ccd-mono">{a.code}</b>
                      <div className="ccd-meta">{a.name}</div>
                    </td>
                    <td className="ccd-td ccd-td-num">
                      <span className="ccd-kpi-down">%{a.artisYuzde}</span>
                    </td>
                    <td className="ccd-td ccd-td-num">{formatTl(a.unitPriceKurus)}</td>
                    <td className="ccd-td">
                      <div className="ccd-mono">{a.faturaNo}</div>
                      <div className="ccd-meta">{a.tedarikci} · {formatTarih(a.tarih)}</div>
                    </td>
                    <td className="ccd-td ccd-td-nowrap">
                      {a.penceseKapandi ? (
                        <span className="ccd-muted">kapandı</span>
                      ) : (
                        <span className="ccd-badge ccd-badge-warning">
                          {a.itirazKalanGun} gün kaldı
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : !veri ? (
        <div className="ccd-inline-note">Yükleniyor…</div>
      ) : veri.faturalar.length === 0 ? (
        <div className="ccd-empty">Bu süzgeçte fatura yok.</div>
      ) : (
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">Fatura</th>
                <th className="ccd-th">Tedarikçi</th>
                <th className="ccd-th ccd-td-num">Satır</th>
                <th className="ccd-th ccd-td-num">Tutar</th>
                <th className="ccd-th">Durum</th>
                <th className="ccd-th">İtiraz <Ipucu id="fiyat-artisi" /></th>
              </tr>
            </thead>
            <tbody>
              {veri.faturalar.map((f) => (
                <tr key={f.id} className="ccd-tr ccd-tr-clickable" onClick={() => setSecili(f.id)}>
                  <td className="ccd-td">
                    <b className="ccd-mono">{f.no}</b>
                    <div className="ccd-meta">{formatTarih(f.date)}</div>
                  </td>
                  <td className="ccd-td">{f.supplier}</td>
                  <td className="ccd-td ccd-td-num">
                    {f.bekleyenSatir > 0
                      ? <span className="ccd-kpi-down">{f.bekleyenSatir}/{f.satirAdedi} bekliyor</span>
                      : <span className="ccd-muted">{f.satirAdedi}</span>}
                  </td>
                  <td className="ccd-td ccd-td-num">{formatTl(f.totalKurus)}</td>
                  <td className="ccd-td ccd-td-nowrap">
                    <span className={`ccd-badge${f.status === 'BAGLANDI' ? ' ccd-badge-success' : f.status === 'KISMEN' ? ' ccd-badge-warning' : ''}`}>
                      {f.status === 'BAGLANDI' ? 'Bağlandı' : f.status === 'KISMEN' ? 'Kısmen' : 'Bekliyor'}
                    </span>
                    {f.artisVar && <div className="ccd-meta ccd-kpi-down">fiyat artışı var</div>}
                  </td>
                  <td className="ccd-td ccd-td-nowrap">
                    {f.itirazKalanGun > 0
                      ? <span className="ccd-meta">{f.itirazKalanGun} gün</span>
                      : <span className="ccd-muted">kapandı</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {secili != null && (
        <AlisDetay id={secili} kapat={() => setSecili(null)} degisti={yukle} />
      )}
      {yeniAcik && (
        <YeniAlis kapat={() => setYeniAcik(false)}
                  eklendi={(id) => { setYeniAcik(false); yukle(); setSecili(id); }} />
      )}
    </>
  );
}
